// Bookmark types - unified from frontend and mobile

import { z } from 'zod';
import type { Question } from './question';

/**
 * Portable Firestore Timestamp shape. Both firebase-admin's Timestamp (Node,
 * backend-only) and the client SDK's Timestamp (browser, mobile) satisfy this
 * interface structurally — they all expose toDate(), toMillis(), seconds,
 * nanoseconds. Declaring it here (instead of importing firebase-admin) keeps
 * the types package portable across Node + browser + React Native without
 * requiring firebase-admin as a transitive dep on client consumers.
 *
 * Stays a TS interface because it carries methods (toDate()/toMillis()); fields
 * of this type are modeled at runtime via z.custom<FirestoreTimestamp>().
 *
 * If more types need a Firestore timestamp field, promote this to common.ts.
 */
export interface FirestoreTimestamp {
  toDate(): Date;
  toMillis(): number;
  seconds: number;
  nanoseconds: number;
}

/**
 * Standard bookmark interface used across all applications
 * Represents a user's saved question with optional notes
 */
export const BookmarkSchema = z.object({
  id: z.string(),
  userId: z.string(),
  questionId: z.string(),
  notes: z.string().optional(),
  createdAt: z.string(), // ISO string
  updatedAt: z.string().optional(), // ISO string
  question: z.custom<Question>().optional(), // Optional populated question data
});
export type Bookmark = z.infer<typeof BookmarkSchema>;

/**
 * Request payload for updating bookmark notes
 */
export const UpdateBookmarkRequestSchema = z.object({
  notes: z.string().optional(),
});
export type UpdateBookmarkRequest = z.infer<typeof UpdateBookmarkRequestSchema>;

/**
 * Request payload for creating a new bookmark
 */
export const CreateBookmarkRequestSchema = z.object({
  questionId: z.string(),
  notes: z.string().optional(),
});
export type CreateBookmarkRequest = z.infer<typeof CreateBookmarkRequestSchema>;

export const BookmarkDocumentSchema = z.object({
  id: z.string(),
  userId: z.string(),
  questionId: z.string(),
  notes: z.string().optional(),
  createdAt: z.custom<FirestoreTimestamp>(),
  updatedAt: z.custom<FirestoreTimestamp>().optional(),
});
export type BookmarkDocument = z.infer<typeof BookmarkDocumentSchema>;
